import { FC, useCallback, useEffect, useState } from 'react'
import { Button, Card, Empty, Image, message, Modal, Space, Spin, Tag } from 'antd'
import {
	ArrowLeftOutlined,
	DownloadOutlined,
	EyeOutlined,
	FileExcelOutlined,
	FileOutlined,
	FilePdfOutlined,
	FileTextOutlined,
	FileWordOutlined,
	FileZipOutlined,
	PictureOutlined,
	PlayCircleOutlined,
	PushpinFilled,
	PushpinOutlined,
	ReadOutlined
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { learningStore } from '../store/learning'
import { authStore } from '../store/auth'
import { fetchDownloadUrl } from '../api/learning'
import {
	LearningResource,
	ResourceType
} from '../helpers/types/learning'
import { RNLearning } from '../helpers/routeNames'
import { PageBreadcrumb } from '../components/PageBreadcrumb'
import { AxiosError } from 'axios'
import '../styles/tiptap.scss'

export const LearningDetail: FC = () => {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const { isLoggedIn } = authStore()
	const {
		selectedMaterial,
		relatedMaterials,
		loading,
		fetchMaterialById,
		logView
	} = learningStore()

	const [isPinned, setIsPinned] = useState(false)

	useEffect(() => {
		if (!id) return
		fetchMaterialById(id)
	}, [id, fetchMaterialById])

	useEffect(() => {
		if (!id || !isLoggedIn || !selectedMaterial) return
		logView(id).catch(() => {})
	}, [id, isLoggedIn, selectedMaterial, logView])

	const handleTogglePin = async () => {
		if (!id) return
		try {
			await learningStore.getState().togglePin(id, isPinned)
			setIsPinned(!isPinned)
			message.success(
				isPinned
					? 'Удалено из избранного'
					: 'Добавлено в избранное'
			)
		} catch (err) {
			if (err instanceof AxiosError) {
				message.error(
					err.response?.data?.message ||
						'Ошибка при изменении избранного'
				)
			}
		}
	}

	if (loading || !selectedMaterial) {
		return (
			<div style={{ textAlign: 'center', padding: 60 }}>
				<Spin size="large" />
			</div>
		)
	}

	const resources = selectedMaterial.resources || []

	return (
		<div style={{ padding: '16px', paddingBottom: '90px' }}>
			<PageBreadcrumb
				items={[
					{ label: 'База знаний', path: RNLearning },
					{ label: selectedMaterial.title }
				]}
			/>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'flex-start',
					marginBottom: 16,
					gap: 12
				}}>
				<Button
					icon={<ArrowLeftOutlined />}
					onClick={() => navigate(RNLearning)}>
					Назад
				</Button>
				{isLoggedIn && (
					<Button
						icon={
							isPinned ? (
								<PushpinFilled />
							) : (
								<PushpinOutlined />
							)
						}
						onClick={handleTogglePin}>
						{isPinned ? 'В избранном' : 'В избранное'}
					</Button>
				)}
			</div>

			<h1 style={{ marginTop: 0 }}>{selectedMaterial.title}</h1>

			{selectedMaterial.description && (
				<p style={{ color: '#555', fontSize: 15 }}>
					{selectedMaterial.description}
				</p>
			)}

			<div
				style={{
					display: 'flex',
					gap: 8,
					flexWrap: 'wrap',
					marginBottom: 20
				}}>
				<Tag color="blue">{selectedMaterial.category}</Tag>
				{selectedMaterial.relayTypeName && (
					<Tag>{selectedMaterial.relayTypeName}</Tag>
				)}
				{!!selectedMaterial.pdfCount && (
					<Tag icon={<FileTextOutlined />}>
						{selectedMaterial.pdfCount} PDF
					</Tag>
				)}
				{!!selectedMaterial.videoCount && (
					<Tag icon={<PlayCircleOutlined />}>
						{selectedMaterial.videoCount} видео
					</Tag>
				)}
			</div>

			{resources.length === 0 ? (
				<Empty description="Ресурсы не найдены" />
			) : (
				<Space
					direction="vertical"
					size={16}
					style={{ width: '100%' }}>
					{resources.map((resource) => (
						<div key={resource.id}>
							{resource.type === ResourceType.PDF ? (
								<PDFResource
									resource={resource}
									materialId={selectedMaterial.id}
								/>
							) : resource.type === ResourceType.VIDEO ? (
								<VideoResource resource={resource} />
							) : resource.type === ResourceType.IMAGE ? (
								<ImageResource resource={resource} />
							) : resource.type === ResourceType.FILE ? (
								<FileResource resource={resource} />
							) : resource.type === ResourceType.TEXT ? (
								<TextResource resource={resource} />
							) : null}
						</div>
					))}
				</Space>
			)}

			{relatedMaterials.length > 0 && (
				<div style={{ marginTop: 32 }}>
					<h2>Связанные материалы</h2>
					<div
						style={{
							display: 'grid',
							gridTemplateColumns:
								'repeat(auto-fill, minmax(250px, 1fr))',
							gap: 12
						}}>
						{relatedMaterials.map((m) => (
							<Card
								key={m.id}
								hoverable
								size="small"
								onClick={() =>
									navigate(`${RNLearning}/${m.id}`)
								}>
								<h4 style={{ margin: 0 }}>
									{m.title}
								</h4>
								<Tag
									color="blue"
									style={{ marginTop: 4 }}>
									{m.category}
								</Tag>
							</Card>
						))}
					</div>
				</div>
			)}
		</div>
	)
}

const PDFResource: FC<{
	resource: LearningResource
	materialId: string
}> = ({ resource, materialId }) => {
	const [downloading, setDownloading] = useState(false)
	const [previewUrl, setPreviewUrl] = useState<string | null>(null)

	const getUrl = useCallback(async (): Promise<string | null> => {
		if (resource.signedUrl) return resource.signedUrl
		try {
			const fresh = await fetchDownloadUrl(materialId, resource.id)
			return fresh.signedUrl ?? null
		} catch {
			return null
		}
	}, [resource, materialId])

	const handleOpen = useCallback(async () => {
		setDownloading(true)
		try {
			const url = await getUrl()
			if (url) window.open(url, '_blank')
			else message.error('Не удалось получить ссылку')
		} finally {
			setDownloading(false)
		}
	}, [getUrl])

	const handlePreview = useCallback(async () => {
		setDownloading(true)
		try {
			const url = await getUrl()
			if (url) setPreviewUrl(url)
			else message.error('Unable to get the file link')
		} finally {
			setDownloading(false)
		}
	}, [getUrl])

	return (
		<Card
			size="small"
			styles={{
				body: { padding: '14px 16px' }
			}}>
			<div
				style={{
					display: 'flex',
					gap: 14,
					alignItems: 'flex-start'
				}}>
				<FilePdfOutlined
					style={{ fontSize: 32, color: '#ff4d4f', flexShrink: 0, marginTop: 2 }}
				/>
				<div style={{ flex: 1, minWidth: 0 }}>
					<div
						style={{
							fontWeight: 600,
							fontSize: 15,
							marginBottom: 2
						}}>
						{resource.title}
					</div>
					{resource.description && (
						<p
							style={{
								margin: '0 0 4px',
								color: '#666',
								fontSize: 13
							}}>
							{resource.description}
						</p>
					)}
					{resource.fileName && (
						<span style={{ color: '#aaa', fontSize: 12 }}>
							{resource.fileName}
							{resource.fileSize
								? ` — ${formatFileSize(resource.fileSize)}`
								: ''}
						</span>
					)}
				</div>
				<Space>
					<Button icon={<DownloadOutlined />} onClick={handleOpen}>
						Download
					</Button>
				<Button
					type="primary"
					icon={<DownloadOutlined />}
					loading={downloading}
					onClick={handlePreview}>
					Открыть
				</Button>
				</Space>
			</div>
			<ResourcePreviewModal
				open={!!previewUrl}
				onCancel={() => setPreviewUrl(null)}
				url={previewUrl}
				title={resource.title}
				kind="pdf"
			/>
		</Card>
	)
}

const VideoResource: FC<{ resource: LearningResource }> = ({
	resource
}) => {
	return (
		<Card size="small">
			<div
				style={{
					display: 'flex',
					gap: 10,
					alignItems: 'center',
					marginBottom: resource.embedUrl ? 12 : 0
				}}>
				<PlayCircleOutlined
					style={{ fontSize: 22, color: '#722ed1', flexShrink: 0 }}
				/>
				<div>
					<div style={{ fontWeight: 600, fontSize: 15 }}>
						{resource.title}
					</div>
					{resource.description && (
						<p style={{ margin: '2px 0 0', color: '#666', fontSize: 13 }}>
							{resource.description}
						</p>
					)}
					{resource.duration != null && (
						<span style={{ color: '#aaa', fontSize: 12 }}>
							{formatDuration(resource.duration)}
						</span>
					)}
				</div>
			</div>
			{resource.embedUrl && (
				<div
					style={{
						position: 'relative',
						paddingBottom: '56.25%',
						height: 0,
						overflow: 'hidden',
						borderRadius: 8
					}}>
					<iframe
						src={resource.embedUrl}
						title={resource.title}
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							height: '100%',
							border: 0
						}}
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
						allowFullScreen
					/>
				</div>
			)}
		</Card>
	)
}

const ImageResource: FC<{ resource: LearningResource }> = ({
	resource
}) => {
	const src = resource.signedUrl || resource.fileUrl
	return (
		<Card size="small">
			<div
				style={{
					display: 'flex',
					gap: 10,
					alignItems: 'center',
					marginBottom: src ? 12 : 0
				}}>
				<PictureOutlined
					style={{ fontSize: 22, color: '#1677ff', flexShrink: 0 }}
				/>
				<div style={{ flex: 1 }}>
					<div style={{ fontWeight: 600, fontSize: 15 }}>
						{resource.title}
					</div>
					{resource.description && (
						<p style={{ margin: '2px 0 0', color: '#666', fontSize: 13 }}>
							{resource.description}
						</p>
					)}
				</div>
			</div>
			{src && (
				<Image
					src={src}
					alt={resource.title}
					style={{ borderRadius: 8, maxWidth: '100%' }}
					placeholder
				/>
			)}
		</Card>
	)
}

const FileResource: FC<{ resource: LearningResource }> = ({
	resource
}) => {
	const [previewUrl, setPreviewUrl] = useState<string | null>(null)
	const previewKind = getFilePreviewKind(resource)
	const getMimeIcon = (mimeType?: string) => {
		if (!mimeType) return <FileOutlined style={{ fontSize: 32, color: '#8c8c8c' }} />
		if (mimeType.includes('word') || mimeType.includes('document'))
			return <FileWordOutlined style={{ fontSize: 32, color: '#1677ff' }} />
		if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || mimeType.includes('csv'))
			return <FileExcelOutlined style={{ fontSize: 32, color: '#52c41a' }} />
		if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('compressed') || mimeType.includes('archive'))
			return <FileZipOutlined style={{ fontSize: 32, color: '#faad14' }} />
		if (mimeType.startsWith('text/'))
			return <FileTextOutlined style={{ fontSize: 32, color: '#722ed1' }} />
		return <FileOutlined style={{ fontSize: 32, color: '#8c8c8c' }} />
	}

	const handleDownload = () => {
		const url = resource.signedUrl || resource.fileUrl
		if (url) window.open(url, '_blank')
		else message.error('Файл недоступен')
	}

	const handlePreview = () => {
		const url = resource.signedUrl || resource.fileUrl
		if (url) setPreviewUrl(url)
		else message.error('File is unavailable')
	}

	return (
		<Card
			size="small"
			styles={{ body: { padding: '14px 16px' } }}>
			<div
				style={{
					display: 'flex',
					gap: 14,
					alignItems: 'flex-start'
				}}>
				<div style={{ flexShrink: 0, marginTop: 2 }}>
					{getMimeIcon(resource.mimeType)}
				</div>
				<div style={{ flex: 1, minWidth: 0 }}>
					<div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>
						{resource.title}
					</div>
					{resource.description && (
						<p style={{ margin: '0 0 4px', color: '#666', fontSize: 13 }}>
							{resource.description}
						</p>
					)}
					{resource.fileName && (
						<span style={{ color: '#aaa', fontSize: 12 }}>
							{resource.fileName}
							{resource.fileSize
								? ` — ${formatFileSize(resource.fileSize)}`
								: ''}
						</span>
					)}
				</div>
				<Space>
					{previewKind && (
						<Button icon={<EyeOutlined />} onClick={handlePreview}>
							Preview
						</Button>
					)}
				<Button
					icon={<DownloadOutlined />}
					onClick={handleDownload}>
					Скачать
				</Button>
				</Space>
			</div>
			{previewKind && (
				<ResourcePreviewModal
					open={!!previewUrl}
					onCancel={() => setPreviewUrl(null)}
					url={previewUrl}
					title={resource.title}
					kind={previewKind}
				/>
			)}
		</Card>
	)
}

type FilePreviewKind = 'office' | 'text'

function getFilePreviewKind(resource: LearningResource): FilePreviewKind | null {
	const mimeType = resource.mimeType?.toLowerCase() || ''
	const extension = resource.fileName?.split('.').pop()?.toLowerCase() || ''

	if (
		mimeType.includes('word') ||
		mimeType.includes('excel') ||
		mimeType.includes('spreadsheet') ||
		mimeType.includes('powerpoint') ||
		['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension)
	) return 'office'

	if (
		mimeType.startsWith('text/') ||
		['csv', 'txt', 'md', 'json', 'xml'].includes(extension)
	) return 'text'

	return null
}

const ResourcePreviewModal: FC<{
	open: boolean
	onCancel: () => void
	url: string | null
	title: string
	kind: 'pdf' | FilePreviewKind
}> = ({ open, onCancel, url, title, kind }) => {
	if (!url) return null

	const src = kind === 'office'
		? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
		: url

	return (
		<Modal
			open={open}
			title={title}
			onCancel={onCancel}
			footer={null}
			width="min(1100px, calc(100vw - 32px))"
			destroyOnClose>
			<iframe
				src={src}
				title={title}
				style={{ width: '100%', height: '75vh', border: 0 }}
			/>
		</Modal>
	)
}

const TextResource: FC<{ resource: LearningResource }> = ({
	resource
}) => {
	if (!resource.content) return null
	return (
		<Card
			size="small"
			styles={{ body: { padding: '20px 24px' } }}>
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: 8,
					marginBottom: 16,
					paddingBottom: 14,
					borderBottom: '1px solid #f0f0f0'
				}}>
				<ReadOutlined style={{ fontSize: 18, color: '#52c41a' }} />
				<span style={{ fontWeight: 600, fontSize: 16 }}>
					{resource.title}
				</span>
			</div>
			{resource.description && (
				<p
					style={{
						color: '#666',
						fontSize: 14,
						marginBottom: 16,
						marginTop: -8
					}}>
					{resource.description}
				</p>
			)}
			{/* Renders TipTap HTML — content is admin-created only */}
			<div
				className="tiptap-prose"
				dangerouslySetInnerHTML={{ __html: resource.content }}
			/>
		</Card>
	)
}

function formatFileSize(bytes: number): string {
	const mb = bytes / (1024 * 1024)
	return mb < 1
		? `${(bytes / 1024).toFixed(1)} КБ`
		: `${mb.toFixed(1)} МБ`
}

function formatDuration(seconds: number): string {
	const mins = Math.floor(seconds / 60)
	const secs = seconds % 60
	return `${mins}:${secs.toString().padStart(2, '0')}`
}

